import React, { useEffect, useState } from 'react';
import { Box, Stack, FormControl, FormLabel, Button, Switch, Sheet, CircularProgress } from '@mui/joy';
import { useAdmin } from '../../contexts/Admin.tsx';
import MonacoEditor from '../../components/admin/MonacoEditor.tsx';
import { PageHeader, SectionCard, PageMessage } from '../../components/admin/Page.tsx';
import ChipsInput from '../../components/admin/ChipsInput.tsx';

export default function Announcement() {
  const { api } = useAdmin();
  const [content, setContent] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [origins, setOrigins] = useState<string[]>([]);
  const [original, setOriginal] = useState({ content: '', enabled: false, origins: [] as string[] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'danger'; text: string } | null>(null);

  useEffect(() => {
    api.getAnnouncement()
      .then(a => {
        if (a) {
          setContent(a.content || '');
          setEnabled(!!a.enabled);
          setOrigins(a.origins || []);
          setOriginal({ content: a.content || '', enabled: !!a.enabled, origins: a.origins || [] });
        }
      })
      .catch(err => setMessage({ type: 'danger', text: err.message }))
      .finally(() => setLoading(false));
  }, [api]);

  const dirty = content !== original.content || enabled !== original.enabled
    || JSON.stringify(origins) !== JSON.stringify(original.origins);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await api.updateAnnouncement(content, enabled, origins);
      setOriginal({ content, enabled, origins: [...origins] });
      setMessage({ type: 'success', text: 'Announcement saved' });
    } catch (err: any) {
      setMessage({ type: 'danger', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    setContent(original.content);
    setEnabled(original.enabled);
    setOrigins(original.origins);
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', position: 'relative', pb: 12 }}>
      <PageHeader
        title="Announcement"
        subtitle="Custom HTML banner shown to mailbox visitors"
        actions={
          <FormControl orientation="horizontal" sx={{ alignItems: 'center', gap: 1 }}>
            <FormLabel sx={{ m: 0 }}>{enabled ? 'Live' : 'Disabled'}</FormLabel>
            <Switch checked={enabled} onChange={e => setEnabled(e.target.checked)} color={enabled ? 'success' : 'neutral'} />
          </FormControl>
        }
      />
      <PageMessage message={message} />

      <Stack spacing={3}>
        <SectionCard title="HTML Content" description="Editor uses Monaco with dark/light following the theme">
          <MonacoEditor value={content} onChange={setContent} language="html" height={500} />
        </SectionCard>

        <SectionCard title="Allowed Origins" description="Empty list shows the announcement everywhere">
          <ChipsInput
            label="Origin allow-list"
            helper="Comma-separated supported"
            values={origins}
            onChange={setOrigins}
            placeholder="example.com, app.example.com"
          />
        </SectionCard>
      </Stack>

      <Sheet
        variant="plain"
        sx={{
          position: 'sticky',
          bottom: 0,
          mt: 3,
          py: 2,
          px: 3,
          mx: -3,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.body',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          zIndex: 5
        }}
      >
        <Button variant="plain" color="neutral" onClick={discard} disabled={!dirty || saving}>Discard</Button>
        <Button onClick={handleSave} loading={saving} disabled={!dirty}>Save Announcement</Button>
      </Sheet>
    </Box>
  );
}
